import { Box, Button, Typography } from "@mui/material";
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { ArrowForwardIos, ArrowBackIos, Margin } from "@mui/icons-material";
import { useStyles } from "./style";
import { NewLearningRequestStep } from "utils/types";
import { SurveyType, LearningRequestWorkflowInput } from "utils/graphql/Global";
import { useTranslation } from "react-i18next";
import { Loader } from "components/loader/loader";
import { SURVEY_DATA, START_LEARNING_REQUEST_FLOW } from "utils/queries";
import * as SurveyTypes from "utils/graphql/SurveyData";
import { useQuery, useMutation } from "@apollo/client/react";
import { SurveyContainer } from "../../../question-types/survey-container/survey-container";
import portalSettingsManager from "utils/portalSettingsManager";
import { mapToResponse, validateAllQuestions } from "../../../shared/validation";
import { useAppDispatch } from "store";
import { logger } from "utils/logger";
import {
  StartLearningRequestWorkflow,
  StartLearningRequestWorkflowVariables,
} from "utils/graphql/StartLearningRequestWorkflow";
import {
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  focusFirstError,
  showAction,
} from "../../../shared/newLearningHelper";
import useBudgetCheckNavigation from "../../../shared/useBudgetCheckNavigation";
import { RenderHTML } from "components/sanitizeHtml/sanitizeHtml";

interface CourseAmendmentProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  onPrevious: () => void;
  onSubmitSuccess?: () => void;
  setDialogTitle?: (title: string) => void;
  isMobile?: boolean;
}
export const CourseAmendment: FC<CourseAmendmentProps> = ({
  onNext,
  onPrevious,
  onSubmitSuccess,
  setDialogTitle,
  isMobile = false,
}) => {
  const classes = useStyles({ isMobile });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const questionRefs: MutableRefObject<Record<string, HTMLDivElement | null>> = useRef({});
  const [questions, setQuestions] = useState<SurveyTypes.SurveyData_survey_questions[]>([]);
  const [survey, setSurvey] = useState<SurveyTypes.SurveyData_survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, any>>({});
  const navigateWithBudgetCheck = useBudgetCheckNavigation();
  const isBudgetCheckEnabled =
    portalSettingsManager.application?.features?.learningRequestBudgetCheck?.IsBudgetCheckEnabled ??
    true; // default to enabled

  const handleAnswerChange = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setValidationErrors((prev) => ({
      ...prev,
      [questionId]: {},
    }));
  }, []);

  const {
    loading,
    data,
    error: queryError,
  } = useQuery<SurveyTypes.SurveyData>(SURVEY_DATA, {
    variables: {
      surveyType: SurveyType.COURSE_AMENDMENT,
    },
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  // Apollo Client v4: Handle errors with useEffect instead of onError
  useEffect(() => {
    if (queryError) {
      logger.error("Failed to load course details data", { error: queryError });
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("course_amendment"),
        message: t("course_details_load_error_alert_message"),
      });
      setQuestions([]);
    }
  }, [queryError, dispatch, t]);

  const [StartLearningRequestFlow] = useMutation<
    StartLearningRequestWorkflow,
    StartLearningRequestWorkflowVariables
  >(START_LEARNING_REQUEST_FLOW);

  const handleSubmit = useCallback(async () => {
    const learnerAnswers = mapToResponse(answers);

    const learningRequestWorkflowInput: LearningRequestWorkflowInput = {
      knowsCourse: true,
      surveyId: survey?.surveyId,
      learnerAnswers,
    };

    logger.info("Submitting course details", {
      surveyId: survey?.surveyId,
      answerCount: learnerAnswers?.length ?? 0,
    });

    showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
      show: true,
      message: t("course_amendment"),
    });

    try {
      await StartLearningRequestFlow({
        variables: {
          payload: learningRequestWorkflowInput,
        },
      });
      logger.info("Course details submitted successfully");
      onSubmitSuccess?.();
      navigateWithBudgetCheck(onNext);
    } catch (error) {
      logger.error("Failed to submit course details", {
        surveyId: survey?.surveyId,
        error: error instanceof Error ? error.message : String(error),
      });

      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("course_amendment"),
        message: t("course_details_save_error"),
      });
    } finally {
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
        show: false,
      });
    }
  }, [answers, survey?.surveyId, StartLearningRequestFlow, onNext, onSubmitSuccess, dispatch, t]);

  const handleNext = useCallback(() => {
    if (!questions.length) return;

    const { isValid, errors } = validateAllQuestions(questions, answers, {
      skipMinCharValidation: true,
    });
    if (!isValid) {
      setValidationErrors(errors);
      // This helps keyboard and screen reader users immediately know where the problem is
      focusFirstError(errors, questionRefs.current);
      return;
    }

    handleSubmit();
  }, [questions, answers, handleSubmit]);

  useEffect(() => {
    if (data?.survey) {
      setSurvey(data.survey as SurveyTypes.SurveyData_survey);
      setQuestions(data.survey.questions as SurveyTypes.SurveyData_survey_questions[]);
      // Clear validation errors when survey loads
      setValidationErrors({});
    }
  }, [data]);

  useEffect(() => {
    setDialogTitle?.(t("course_amendment"));
  }, [setDialogTitle, t]);

  return (
    <Box className={classes.container}>
      <Loader isDisplay={loading}></Loader>
      <Box className={classes.headerDescription}>
        <RenderHTML
          className={classes.headerDescription}
          content={t("NLR_course_details_description")}
        />
      </Box>
      <Box className={classes.contentCard}>
        <Typography variant="h2" className={classes.cardTitle}>
          {t("course_amendment")}
        </Typography>
        <Box className={classes.fieldsContainer}>
          {survey?.questions?.map((question, index) => {
            return (
              <div
                key={question.questionId}
                ref={(el) => {
                  questionRefs.current[question.questionId] = el;
                }}
              >
                <Box className={classes.questionItem}>
                  <SurveyContainer
                    question={question}
                    questionIndex={index}
                    answer={answers[question.questionId]}
                    onAnswerChange={handleAnswerChange}
                    validationErrors={validationErrors[question.questionId] || {}}
                    maxCharacters={
                      portalSettingsManager.surveyCharacterMinMaxThreshold
                        ?.surveyLearnerCommentMaxLength
                    }
                  />
                </Box>
              </div>
            );
          })}
        </Box>
        <Box className={classes.buttonGroup}>
          <Button
            id="course-details-back-btn"
            type="button"
            variant="outlined"
            onClick={onPrevious}
            startIcon={<ArrowBackIos />}
            aria-label={t("back")}
            className={classes.outlineButton}
          >
            {t("back")}
          </Button>

          {isBudgetCheckEnabled ? (
            <Button
              id="course-details-continue-to-budget-check-btn"
              type="button"
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIos />}
              className={classes.primaryButton}
              aria-label={t("continue_to_budget_check")}
            >
              {t("continue_to_budget_check")}
            </Button>
          ) : (
            <Button
              id="budget-check-continue-to-commissioning-btn"
              type="button"
              variant="contained"
              className={classes.primaryButton}
              endIcon={<ArrowForwardIos />}
              onClick={handleNext}
              aria-label={t("continue")}
            >
              {t("continue")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
